export const FINANCE_ADMIN_ROLES = ["super_admin", "director"];

export const canManageFeeStructures = (user) => {
  const roles = new Set([user?.role, ...(user?.roles || [])].filter(Boolean));
  return FINANCE_ADMIN_ROLES.some((role) => roles.has(role));
};

export const parseNgnToMinor = (value) => {
  const normalized = String(value ?? "").trim().replace(/,/g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) return null;
  const [naira, kobo = ""] = normalized.split(".");
  const minor = Number(naira) * 100 + Number(kobo.padEnd(2, "0"));
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
};

export const formatMinorNgn = (minor) => `₦${((Number(minor) || 0) / 100).toLocaleString(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})}`;

export const structureTotalMinor = (structure) => (structure?.items || []).reduce(
  (total, item) => total + (Number(item.amount_minor) || 0),
  0,
);

export const normalizeItemId = (name, fallbackIndex = 0) => {
  const normalized = String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 100);
  return normalized || `fee_item_${fallbackIndex + 1}`;
};

export const validateFeeStructureForm = (form) => {
  const errors = {};
  if (!form.session_id) errors.session_id = "Academic session is required";
  if (!form.term_id) errors.term_id = "Academic term is required";
  if (!form.program_id) errors.program_id = "Program is required";
  if (!form.class_id) errors.class_id = "Class is required";
  if (!form.items?.length) errors.items = "Add at least one fee item";

  const itemIds = new Set();
  (form.items || []).forEach((item, index) => {
    const key = `item_${index}`;
    const name = String(item.name || "").trim();
    const itemId = String(item.item_id || normalizeItemId(name, index)).trim();
    if (!name) errors[key] = "Fee item name is required";
    else if (!parseNgnToMinor(item.amount)) errors[key] = "Enter an amount greater than zero with at most two decimal places";
    else if (itemIds.has(itemId)) errors[key] = "Fee item identifiers must be unique";
    itemIds.add(itemId);
  });
  return errors;
};

export const buildFeeStructurePayload = (form) => ({
  session_id: form.session_id,
  term_id: form.term_id,
  program_id: form.program_id,
  class_id: form.class_id,
  applies_to: form.applies_to || "all",
  student_type: form.student_type || "all",
  is_active: Boolean(form.is_active),
  items: form.items.map((item, index) => ({
    item_id: String(item.item_id || normalizeItemId(item.name, index)).trim(),
    name: String(item.name).trim(),
    amount_minor: parseNgnToMinor(item.amount),
    category: String(item.category || "").trim() || null,
    description: String(item.description || "").trim() || null,
  })),
});

export const structureScopeKey = (structure) => [
  structure.session_id,
  structure.term_id,
  structure.program_id,
  structure.class_id || "__all_classes__",
  structure.student_type || "all",
  structure.applies_to || "all",
].join(":");
